import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { ErrorBanner, LimitPill, Spinner, Toast } from '../components/ui';
import { api, type AccountLimitOption, type PreparedAdvert } from '../lib/api';

/**
 * Add Advert: WordPress product id -> AI-generated payload -> review -> publish.
 *
 * The three-step shape is deliberate. The AI agent takes several seconds and
 * several OLX calls, and its output needs a human glance before it becomes a live
 * advert. Publishing is a separate call that reuses the reviewed payload, so what
 * the operator approved is exactly what gets sent.
 */
export function AddAdvertPage() {
  const [wpProductId, setWpProductId] = useState('');
  const [accountId, setAccountId] = useState<number | null>(null);
  const [hint, setHint] = useState('');
  const [prepared, setPrepared] = useState<PreparedAdvert | null>(null);
  const [published, setPublished] = useState<{ url: string; status: string; warnings: string[] } | null>(null);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: api.accounts.list });

  /**
   * Once a category is known, the dropdown is re-driven by the limits endpoint so
   * each account shows its real availability for *that* category rather than a
   * generic list.
   */
  const accountOptions = useQuery({
    queryKey: ['account-options', prepared?.agent.categoryId, prepared?.product.id],
    queryFn: () => api.limits.accountOptions(prepared!.agent.categoryId, prepared!.product.id),
    enabled: Boolean(prepared),
  });

  const prepare = useMutation({
    mutationFn: () =>
      api.adverts.prepare({
        accountId: accountId!,
        wpProductId: Number(wpProductId),
        ...(hint ? { hint } : {}),
      }),
    onSuccess: (result) => {
      setPrepared(result);
      setPublished(null);
    },
  });

  const publish = useMutation({
    mutationFn: (force: boolean) =>
      api.adverts.publish({
        accountId: accountId!,
        wpProductId: Number(wpProductId),
        categoryId: prepared!.agent.categoryId,
        payload: prepared!.payload,
        force,
      }),
    onSuccess: (result) => {
      setPublished({ url: result.advert.url, status: result.advert.status, warnings: result.warnings });
      setPrepared(null);
    },
  });

  const selectedOption = accountOptions.data?.find((option) => option.accountId === accountId);
  const blocked = (prepared?.blockers.length ?? 0) > 0;
  const limitBlocked = prepared ? !prepared.limit.allowed : false;
  const duplicateBlocked = Boolean(prepared?.duplicates.onThisAccount);

  const reset = () => {
    setPrepared(null);
    setPublished(null);
    prepare.reset();
    publish.reset();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Add advert</h1>
        <p className="mt-1 text-sm text-slate-400">
          The AI agent picks the OLX category, reads its attribute schema and fills the required fields from the shop
          description.
        </p>
      </div>

      {/* Step 1 — source and target */}
      <section className="card p-6">
        <StepHeading step={1} title="Source product and target account" />

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="wp-id">
              WordPress product ID
            </label>
            <input
              id="wp-id"
              className="input"
              value={wpProductId}
              onChange={(event) => {
                setWpProductId(event.target.value.replace(/\D/g, ''));
                reset();
              }}
              placeholder="4821"
              inputMode="numeric"
            />
            <p className="mt-1.5 text-xs text-slate-500">The `ID` column of the WooCommerce product post.</p>
          </div>

          <div>
            <label className="label" htmlFor="account">
              OLX account
            </label>
            <select
              id="account"
              className="input"
              value={accountId ?? ''}
              onChange={(event) => setAccountId(event.target.value ? Number(event.target.value) : null)}
            >
              <option value="">Select an account…</option>
              {accounts.data?.map((account) => (
                <option key={account.id} value={account.id} disabled={!account.authorized || !account.enabled}>
                  {account.label ?? account.name}
                  {!account.authorized ? ' — not authorized' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="label" htmlFor="hint">
            Instructions for the agent (optional)
          </label>
          <input
            id="hint"
            className="input"
            value={hint}
            onChange={(event) => setHint(event.target.value)}
            placeholder="Състоянието е употребявано, категорията трябва да е Климатици."
          />
        </div>

        <button
          type="button"
          className="btn-primary mt-5"
          disabled={!wpProductId || !accountId || prepare.isPending}
          onClick={() => prepare.mutate()}
        >
          {prepare.isPending ? <Spinner /> : null}
          {prepare.isPending ? 'Agent is working…' : 'Generate with AI'}
        </button>

        {prepare.isError ? (
          <div className="mt-4">
            <ErrorBanner error={prepare.error} />
          </div>
        ) : null}
      </section>

      {/* Step 2 — review */}
      {prepared ? (
        <>
          <section className="card p-6">
            <StepHeading step={2} title="Review the generated advert" />

            <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_20rem]">
              <div className="space-y-5">
                <Field label="Category">
                  <p className="text-sm text-slate-200">{prepared.agent.categoryPath}</p>
                  <p className="mt-0.5 text-xs text-slate-500">id {prepared.agent.categoryId}</p>
                </Field>

                <Field label={`Title · ${prepared.payload.title.length} chars`}>
                  <p className="text-sm text-slate-200">{prepared.payload.title}</p>
                </Field>

                <Field label={`Description · ${prepared.payload.description.length} chars`}>
                  <pre className="max-h-56 overflow-auto rounded-lg bg-surface p-3 text-xs whitespace-pre-wrap text-slate-300">
                    {prepared.payload.description}
                  </pre>
                </Field>

                <Field label="Attributes mapped by the agent">
                  {prepared.payload.attributes.length === 0 ? (
                    <p className="text-sm text-slate-500">None required for this category.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {prepared.payload.attributes.map((attribute) => (
                        <span key={attribute.code} className="badge border border-border-subtle bg-surface font-mono">
                          {attribute.code}={attribute.value ?? attribute.values?.join('|')}
                        </span>
                      ))}
                    </div>
                  )}
                </Field>

                {prepared.agent.reasoning ? (
                  <Field label="Agent reasoning">
                    <p className="text-sm text-slate-400 italic">{prepared.agent.reasoning}</p>
                  </Field>
                ) : null}

                <details className="text-xs text-slate-500">
                  <summary className="cursor-pointer hover:text-slate-300">
                    Tool trace · {prepared.agent.steps} steps · {prepared.agent.provider}/{prepared.agent.model} ·{' '}
                    {(prepared.agent.durationMs / 1000).toFixed(1)}s
                  </summary>
                  <ol className="mt-2 space-y-1">
                    {prepared.agent.toolTrace.map((entry, index) => (
                      <li key={index} className="font-mono">
                        {entry.ok ? '✓' : '✗'} {entry.tool}({JSON.stringify(entry.args).slice(0, 90)})
                      </li>
                    ))}
                  </ol>
                </details>
              </div>

              <aside className="space-y-4">
                <div className="rounded-lg border border-border-subtle bg-surface p-4">
                  <p className="label mb-2">Price</p>
                  <p className="text-2xl font-semibold tabular-nums">
                    {prepared.payload.price?.value ?? '—'}{' '}
                    <span className="text-sm text-slate-500">{prepared.payload.price?.currency}</span>
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Shop price: {prepared.product.price ?? '—'}</p>
                </div>

                <div className="rounded-lg border border-border-subtle bg-surface p-4">
                  <p className="label mb-2">Images · {prepared.payload.images?.length ?? 0}</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(prepared.payload.images ?? []).slice(0, 6).map((image) => (
                      <img
                        key={image.url}
                        src={image.url}
                        alt=""
                        loading="lazy"
                        className="aspect-square rounded object-cover"
                      />
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-border-subtle bg-surface p-4">
                  <p className="label mb-2">Free-advert limit</p>
                  <LimitPill
                    used={prepared.limit.used}
                    quota={prepared.limit.quota}
                    windowDays={prepared.limit.rule?.windowDays}
                    unlimited={prepared.limit.unlimited}
                  />
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">{prepared.limit.reason}</p>
                </div>
              </aside>
            </div>

            {prepared.agent.warnings.length > 0 ? (
              <div className="mt-5">
                <Toast tone="warning">
                  <ul className="list-inside list-disc space-y-1">
                    {prepared.agent.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </Toast>
              </div>
            ) : null}
          </section>

          {/* Step 3 — account choice under limit pressure, then publish */}
          <section className="card p-6">
            <StepHeading step={3} title="Confirm the account and publish" />

            {blocked ? (
              <div className="mt-4">
                <Toast tone={duplicateBlocked ? 'error' : 'warning'}>
                  <p className="font-medium">Blocked before publishing:</p>
                  <ul className="mt-1.5 list-inside list-disc space-y-1">
                    {prepared.blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                  {limitBlocked ? (
                    <p className="mt-2">Pick an account below that still has a free slot in this category.</p>
                  ) : null}
                </Toast>
              </div>
            ) : null}

            <div className="mt-5">
              <p className="label mb-2">Availability for this category, per account</p>
              {accountOptions.isLoading ? (
                <Spinner className="h-5 w-5 text-slate-500" />
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {accountOptions.data?.map((option) => (
                    <AccountOptionCard
                      key={option.accountId}
                      option={option}
                      selected={option.accountId === accountId}
                      onSelect={() => setAccountId(option.accountId)}
                    />
                  ))}
                </div>
              )}
            </div>

            {prepared.duplicates.onOtherAccounts.length > 0 ? (
              <p className="mt-4 text-xs text-slate-400">
                Already live elsewhere:{' '}
                {prepared.duplicates.onOtherAccounts.map((duplicate) => (
                  <span key={duplicate.accountId} className="mr-2">
                    {duplicate.olxUrl ? (
                      <a href={duplicate.olxUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                        {duplicate.accountName}
                      </a>
                    ) : (
                      duplicate.accountName
                    )}
                  </span>
                ))}
              </p>
            ) : null}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="btn-primary"
                disabled={publish.isPending || duplicateBlocked || (limitBlocked && selectedOption?.limit.allowed !== true)}
                onClick={() => publish.mutate(false)}
              >
                {publish.isPending ? <Spinner /> : null}
                Publish to {accounts.data?.find((a) => a.id === accountId)?.name ?? 'OLX'}
              </button>

              {limitBlocked && !duplicateBlocked ? (
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={publish.isPending}
                  onClick={() => publish.mutate(true)}
                  title="Publish anyway. Use when the local counter is behind reality; OLX may still return 'limited'."
                >
                  Publish anyway
                </button>
              ) : null}

              <button type="button" className="btn-ghost" onClick={reset}>
                Discard
              </button>
            </div>

            {publish.isError ? (
              <div className="mt-4">
                <ErrorBanner error={publish.error} />
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {published ? (
        <Toast tone={published.status === 'limited' ? 'warning' : 'success'}>
          <p className="font-medium">
            Published with status <code>{published.status}</code>.{' '}
            <a href={published.url} target="_blank" rel="noreferrer" className="underline">
              Open on OLX
            </a>
          </p>
          {published.warnings.length > 0 ? (
            <ul className="mt-2 list-inside list-disc space-y-1">
              {published.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </Toast>
      ) : null}
    </div>
  );
}

function AccountOptionCard({
  option,
  selected,
  onSelect,
}: {
  option: AccountLimitOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const disabled = !option.authorized || Boolean(option.duplicateAdvertId);

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={clsx(
        'rounded-lg border p-3 text-left transition-colors',
        selected ? 'border-accent bg-accent-soft/40' : 'border-border-subtle bg-surface hover:border-slate-500',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">{option.label ?? option.accountName}</span>
        <LimitPill
          used={option.limit.used}
          quota={option.limit.quota}
          windowDays={option.limit.rule?.windowDays}
          unlimited={option.limit.unlimited}
        />
      </div>
      <p className="mt-1.5 text-xs leading-snug text-slate-500">
        {!option.authorized
          ? 'Not authorized — connect it on the Accounts tab.'
          : option.duplicateAdvertId
            ? `Already published here (advert ${option.duplicateAdvertId}).`
            : option.limit.reason}
      </p>
    </button>
  );
}

function StepHeading({ step, title }: { step: number; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent-soft text-xs font-semibold text-white">
        {step}
      </span>
      <h2 className="text-base font-semibold">{title}</h2>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="label">{label}</p>
      {children}
    </div>
  );
}
